/**
 * WooCommerce connector — type-specific config + credentials schemas.
 *
 * Validated at the handler edge (create/update connector) for
 * `ConnectorType = 'woocommerce'`. Stored as JSON in `connectors.config`
 * (config) and `connectors.credentials_json` (credentials, inline after
 * master-migration 0008_drop_credentials_secret_arn).
 */
import { z } from 'zod';
/**
 * Public config for a WooCommerce connector. Lives in `connectors.config`.
 *
 * `url` is the base URL of the store (no trailing slash, no `/wp-json` suffix).
 * `apiVersion` defaults to `v3`; older stores can pin it explicitly.
 * `verifySsl` default `true`; set to `false` ONLY for staging hosts with
 * self-signed certs — do not persist for production tenants.
 */
export const WooCommerceConfigSchema = z.object({
    url: z.url().describe('Base URL of the WooCommerce store'),
    apiVersion: z.enum(['v3']).default('v3'),
    verifySsl: z.boolean().default(true),
    timeoutMs: z.number().int().positive().max(60_000).default(30_000),
});
/**
 * Credentials for a WooCommerce connector. Lives in `connectors.credentials_json`.
 *
 * Generate via the WooCommerce admin: *Settings → Advanced → REST API →
 * Add key*. Use `read` permissions (read-only scope).
 */
export const WooCommerceCredentialsSchema = z.object({
    consumerKey: z
        .string()
        .min(1)
        .describe('Consumer key generated in WooCommerce admin'),
    consumerSecret: z
        .string()
        .min(1)
        .describe('Consumer secret paired with the consumer key'),
});
/**
 * Cursor shape for incremental sync. `modified_after` is an ISO-8601
 * timestamp; the first sync sets it to `null` and the adapter performs a
 * full-reload. Each subsequent run advances the cursor to the most recent
 * `date_modified_gmt` observed in the batch.
 */
export const WooCommerceCursorSchema = z.object({
    modifiedAfter: z.iso.datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=woocommerce.js.map